import { createLogger } from '@/lib/logger';
import type { Cart, CartLine } from './core';
import type { SimpleCartService } from './simple-cart-service';

type ProposedProperties = Record<string, string | number | boolean | null>;

type ProposedPropertiesByLineKey = Map<string, ProposedProperties>;

type LineItemsDatalayer = {
  lineItems: Record<
    string,
    {
      compareAtPrice: number | null;
      fullPrice: number;
      discounts: { amount: number; source: string }[];
    }
  >;
};

type LineItemPropertiesUpdateContext = {
  cart: Cart;
  cartApi: SimpleCartService;
  proposedPropertiesByLineKey?: ProposedPropertiesByLineKey;
  lineItemsDatalayer: LineItemsDatalayer;
};

type LineItemPropertiesUpdateEvent = (
  ctx: LineItemPropertiesUpdateContext,
) => Promise<LineItemPropertiesUpdateContext>;

type LineItemPropertiesUpdateEventPhase = 'preApply' | 'apply';

type LineItemPropertiesUpdateEventWithMeta = LineItemPropertiesUpdateEvent & {
  __phase?: LineItemPropertiesUpdateEventPhase;
};

const normalizeToString = (value: string | number | boolean): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return value ? 'true' : 'false';
};

const areSubsetPropertiesEqual = (
  current: Record<string, string>,
  proposed: ProposedProperties,
): boolean => {
  return Object.entries(proposed).every(([key, proposedValue]) => {
    if (proposedValue === null) return !(key in current);
    return (current[key] ?? '') === normalizeToString(proposedValue);
  });
};

const getOrInitProposedPropertiesByLineKey = (
  ctx: LineItemPropertiesUpdateContext,
): ProposedPropertiesByLineKey => {
  if (!ctx.proposedPropertiesByLineKey) {
    ctx.proposedPropertiesByLineKey = new Map();
  }
  return ctx.proposedPropertiesByLineKey;
};

const addProposedPropertiesForLineKey = (
  ctx: LineItemPropertiesUpdateContext,
  lineKey: string,
  proposed: ProposedProperties,
): void => {
  const byKey = getOrInitProposedPropertiesByLineKey(ctx);
  const existing = byKey.get(lineKey);
  byKey.set(lineKey, { ...(existing ?? {}), ...proposed });
};

const proposeLineItemProperties = (
  ctx: LineItemPropertiesUpdateContext,
  lineKey: string,
  proposed: ProposedProperties,
): void => {
  addProposedPropertiesForLineKey(ctx, lineKey, proposed);
};

/**
 * Event-chain style executor that can update line item properties via `POST /cart/change.js`.
 *
 * Core invariant: it ONLY performs a change if the proposed properties don't already match
 * the current cart line's properties. This prevents infinite change loops.
 */
export class LineItemPropertiesUpdateChain {
  private eventChain: LineItemPropertiesUpdateEventWithMeta[] = [];
  private logger = createLogger('line-item-properties-update-chain');

  public addEvent(event: LineItemPropertiesUpdateEvent) {
    this.eventChain.push(event);
  }

  public async execute(ctx: LineItemPropertiesUpdateContext) {
    getOrInitProposedPropertiesByLineKey(ctx);
    this.logger.info(
      `Executing line item properties update chain (${this.eventChain.length} events)`,
    );

    const preApplyEvents = this.eventChain.filter((e) => (e.__phase ?? 'preApply') !== 'apply');
    const applyEvents = this.eventChain.filter((e) => (e.__phase ?? 'preApply') === 'apply');

    for (const event of preApplyEvents) {
      ctx = await event(ctx);
    }

    for (const event of applyEvents) {
      ctx = await event(ctx);
    }
    return ctx;
  }

  /**
   * Helper event:
   * - Adds a programmatic proposal to the context accumulator.
   */
  public static proposePropertiesForLineKeyEvent(
    lineKey: string,
    proposed: ProposedProperties,
  ): LineItemPropertiesUpdateEvent {
    return async (ctx) => {
      addProposedPropertiesForLineKey(ctx, lineKey, proposed);
      return ctx;
    };
  }

  /**
   * Final apply event:
   * - Applies all accumulated proposals to the cart via `cart/change.js`.
   *
   * Note: Shopify applies changes one line item at a time, so this will result in
   * up to one request per affected line item (but only in this final phase).
   */
  public static applyProposedPropertiesEvent(): LineItemPropertiesUpdateEvent {
    const logger = createLogger('line-item-properties-update-chain');

    const event: LineItemPropertiesUpdateEventWithMeta = async (ctx) => {
      const byKey = getOrInitProposedPropertiesByLineKey(ctx);
      if (byKey.size === 0) return ctx;

      for (const [lineKey, proposed] of byKey.entries()) {
        const cartLine: CartLine | undefined = ctx.cart.items.find((i) => i.key === lineKey);
        if (!cartLine) continue;

        const currentProps = cartLine.properties ?? {};
        if (areSubsetPropertiesEqual(currentProps, proposed)) continue;

        const nextProps: Record<string, any> = { ...currentProps };
        for (const [k, v] of Object.entries(proposed)) {
          if (v === null) {
            delete nextProps[k];
          } else {
            nextProps[k] = normalizeToString(v);
          }
        }

        logger.info('Updating line item properties via cart/change.js', {
          lineKey,
          proposed,
        });

        // Deliberately not writing `cartLine.quantity` back: the proposal was
        // computed from a snapshot, and the user may have changed the quantity
        // since. `setPropertiesByKey` re-reads the live quantity inside the
        // mutation queue instead.
        try {
          const updatedCart = await ctx.cartApi.setPropertiesByKey(lineKey, nextProps);
          ctx = { ...ctx, cart: updatedCart };
        } catch (error) {
          logger.warn('Skipped property update, line could not be resolved', { lineKey, error });
        }
      }

      byKey.clear();
      return ctx;
    };

    event.__phase = 'apply';
    return event;
  }
}

export type {
  LineItemPropertiesUpdateContext,
  LineItemPropertiesUpdateEvent,
  LineItemsDatalayer,
  ProposedProperties,
  ProposedPropertiesByLineKey,
};

export { proposeLineItemProperties };
